import { SubmitClaim } from "commonAssets/components/Forms/SubmitClaim";
import { ErrorHandler } from "commonAssets/utils/ErrorHandler";
import {
    appPayMethod,
    reCAPTCHAv3,
    i18n,
    loading,
    outputDiv,
} from "commonAssets/js/constants";
import { clearMessage } from "commonAssets/lib/clearMessage";
import { localDatetime } from "commonAssets/lib/localDatetime";
import { ConfirmEmailAddress } from "commonAssets/lib/ConfirmEmailAddress";
import { dstProcess } from "commonAssets/components/FormBase/DigitalDisbursements";
import { modal } from "commonAssets/lib/modal";
import { toggleFieldError } from "commonAssets/lib/toggleFieldError";
import { createElement } from "commonAssets/lib/createElement";
import { loadingGif } from "commonAssets/lib/loadingGif";
import { validateField } from "commonAssets/lib/validateField";
import { uniformClaimDeadline } from "commonAssets/lib/uniformClaimDeadline";
import formDefault from "../../docs/formDefault.json";
import { mergePreservedClaimSections } from "../preserveClaimSections.js";

export class customSubmitClaim extends SubmitClaim {
    async form() {
        const form = formDefault.claim_form[this.lang];
        this.init_claim(form);
    }

    /**
     * Deficiency-portal init_claim override (WP-1342).
     *
     * Literal copy of the shared `SubmitClaim.init_claim()` with two
     * site-local insertions per the Deficiency Portal Workflow:
     *  1. Remove the stock `#claim_form_alert` after the guard renders.
     *  2. Gate access on `cm_data.ClassMemberStatus`: allow only members
     *     with an active "Deficient" status that is editable; deny all
     *     others (fail closed) before OTP / form render.
     * Generic filing is disabled via `generic_claim.display = false`, so the
     * inherited skip-guard path never renders.
     */
    init_claim(form) {
        this.claim.api
            .claimsDeadlineGet({ domain: this.claim.data.domain })
            .then((result) => {
                // Claim Deadline - Default Pacific DateTime
                let datetime;
                if (Boolean(result.data.pacific_datetime)) {
                    datetime = result.data.pacific_datetime;
                } else {
                    datetime = `<pre>Error: datetime</pre>`;
                }

                let claim_deadline;
                if (Boolean(result.data.claim_deadline)) {
                    claim_deadline = result.data.claim_deadline;
                }

                // Error Handling
                if (result.data.error) {
                    this.setup_result_data_error(result);
                    return;
                }

                const container = createElement("div", {
                    class: "display-form",
                });

                /**
                 * Claim Guard
                 */
                if (form.form_options.notice_confirmation.display === true) {
                    // Guard
                    container.appendChild(this.guard(form, datetime));
                    outputDiv.appendChild(container);

                    // Deficiency portal: drop the stock claim-form deadline alert.
                    document.getElementById("claim_form_alert")?.remove();

                    const zero_zero_id =
                        document.getElementById("zero_zero_id");
                    const confirmation_code =
                        document.getElementById("confirmation_code");
                    const skip_guard = document.getElementById("skip-guard");

                    // Notice Id and Confirmation Code
                    [zero_zero_id, confirmation_code].forEach(function (input) {
                        input.addEventListener("change", validateField);
                    });

                    // Skip Guard
                    if (skip_guard) {
                        skip_guard.addEventListener("click", () => {
                            document.getElementById("guard").remove();
                            // New Claim Form
                            container.appendChild(
                                this.claim_form(form, datetime)
                            );
                            container.scrollIntoView({ behavior: "smooth" });
                            outputDiv.appendChild(container);

                            // Init claim fields
                            this.init_claim_fields(
                                this.claim,
                                form,
                                claim_deadline
                            );
                        });
                    }

                    // ada compliance updates - added
                    document
                        .getElementById("submit-guard")
                        .addEventListener("click", () => {
                            document
                                .getElementById("guard")
                                .classList.add("was-validated");
                        });

                    // Validate Guard Fields
                    document
                        .getElementById("guard")
                        .addEventListener("submit", (e) => {
                            e.preventDefault();

                            let valid = true;
                            const member = {
                                domain: this.claim.data.domain,
                                zero_zero_id: zero_zero_id.value.toUpperCase(),
                                confirmation_code: confirmation_code.value,
                            };

                            clearMessage();

                            // Validate guard fields
                            [zero_zero_id, confirmation_code].forEach(
                                function (input) {
                                    valid = validateField(input) && valid;
                                }
                            );
                            if (!valid) return;

                            loadingGif(loading, "show");

                            // Retrieve class member data (use POST to pass notice id and confirmation code privately)
                            this.claim.api
                                .classMemberPost({}, member)
                                .then((result) => {
                                    // Check for error message
                                    if (result.data.error) {
                                        toggleFieldError(
                                            zero_zero_id,
                                            true,
                                            ""
                                        );
                                        toggleFieldError(
                                            confirmation_code,
                                            true,
                                            ""
                                        );
                                        this.errorHandler.log_exception(
                                            new Error(
                                                "Error loading class member",
                                                { cause: result }
                                            )
                                        );
                                        this.errorHandler.display_modal(result);
                                        return;
                                    }

                                    // Add notice id and confirmation code to claim data
                                    this.claim.data.zero_zero_id =
                                        member.zero_zero_id;
                                    this.claim.data.confirmation_code =
                                        member.confirmation_code;

                                    // Deficiency gating (WP-1342): correction-only portal.
                                    // Allow only an active "Deficient" status that is editable.
                                    // cm_data may arrive as an object or a JSON
                                    // string depending on environment; normalize
                                    // before reading (malformed -> deny).
                                    const raw_cm_data = result.data.claim_id
                                        ? result.data.class_member?.cm_data
                                        : result.data.cm_data;
                                    let cm_data = raw_cm_data;
                                    if (typeof raw_cm_data === "string") {
                                        try {
                                            cm_data = JSON.parse(raw_cm_data);
                                        } catch {
                                            cm_data = null;
                                        }
                                    }
                                    const statuses = cm_data?.ClassMemberStatus;
                                    const is_editable_deficient =
                                        Array.isArray(statuses) &&
                                        statuses.some(
                                            (status) =>
                                                (status?.Active === true ||
                                                    status?.Active === 1 ||
                                                    status?.Active === "1") &&
                                                status?.Label === "Deficient" &&
                                                (status.CanEdit === true ||
                                                    status.CanEdit === 1 ||
                                                    status.CanEdit === "1")
                                        );

                                    if (!is_editable_deficient) {
                                        loadingGif(loading, "hide", 250);
                                        modal(
                                            i18n.getTranslation("error"),
                                            "<p class='mb-0 text-center'>This portal is not open to new claims. If you received a deficiency notice, please contact the settlement administrator for assistance.</p>"
                                        );
                                        return;
                                    }

                                    // Define the environment for the OTP
                                    const environment =
                                        this.claim.data.domain.split(".")[0]; // ada compliance - added

                                    if (result.data.claim_id) {
                                        this.claim.data.claim_id =
                                            result.data.claim_id;
                                        this.claim.class_member = result.data;
                                        if (environment !== "local") {
                                            this.send_otp(
                                                form,
                                                container,
                                                result.data,
                                                claim_deadline
                                            );
                                            return;
                                        }
                                    }

                                    // Populate claim form
                                    this.populate_claim_form(
                                        form,
                                        container,
                                        result.data,
                                        claim_deadline
                                    );
                                })
                                .catch((error) => {
                                    this.errorHandler.log_exception(
                                        new Error(
                                            "Error loading class member",
                                            { cause: error }
                                        )
                                    );
                                    this.errorHandler.display_modal(error);
                                });
                        });
                } else {
                    /**
                     * No Claim Guard
                     */
                    // Edit claim link
                    this.edit_claim(form);

                    // New Claim Form
                    const deadlineSpan = uniformClaimDeadline(claim_deadline);
                    container.appendChild(this.claim_form(form, deadlineSpan));
                    outputDiv.appendChild(container);

                    // Init claim fields
                    this.init_claim_fields(this.claim, form, claim_deadline);
                }

                loadingGif(loading, "hide", 250);
            })
            .catch((error) => {
                this.errorHandler.log_exception(
                    new Error("Error fetching claim deadline", { cause: error })
                );
                this.errorHandler.display(error);
            });
    }

    async post_claim(claim, s3env) {
        mergePreservedClaimSections(claim);
        return super.post_claim(claim, s3env);
    }

    process_claim(e, claim, deadline) {
        console.log("[customSubmitClaim:process_claim]");

        e.preventDefault();
        document.getElementById("submit-claim").disabled = true;

        if (claim.flags.sent) return;

        clearMessage();

        claim.flags.valid = true;
        claim.data.fields = {};

        this.init_claim_inputs();
        this.format_claim_data(claim);

        claim.data.contact.date = localDatetime();

        if (document.getElementById("documentation")) {
            this.process_claim_files(this.claim);
        }

        if (
            document.getElementById("email_address") &&
            document.getElementById("confirm_email_address")
        ) {
            ConfirmEmailAddress("process", this.claim);
        }

        // Enforce Option A and/or Option B selection (BE does not enforce)
        const optionASelected =
            document.querySelectorAll(
                "input[name='checkbox-option-a-contemporaneously-submitted-complaint']:checked"
            ).length > 0;
        const optionBSelected =
            document.querySelectorAll(
                "input[name='checkbox-option-b-proof-of-price-overcharge']:checked"
            ).length > 0;

        if (!optionASelected && !optionBSelected) {
            claim.flags.valid = false;
            modal(
                i18n.getTranslation("error"),
                "Please select Option A and/or Option B."
            );
        }

        // Enforce at least one uploaded supporting document
        if (document.getElementById("documentation")) {
            const fileInput = document.getElementById("file-upload");
            const existingFileCount =
                (Array.isArray(this.claim?.class_member?.claim_files)
                    ? this.claim.class_member.claim_files.length
                    : 0) ||
                (Array.isArray(this.claim?.preservedClaimSections?.files)
                    ? this.claim.preservedClaimSections.files.length
                    : 0);

            if (this.claim.uploads.length === 0 && existingFileCount === 0) {
                claim.flags.valid = false;
                if (fileInput) toggleFieldError(fileInput, true, "Required");
                modal(
                    i18n.getTranslation("error"),
                    "Supporting documentation is required for all submissions."
                );
            }
        }

        // Run DST payment validation when enabled, unless overridden
        if (appPayMethod && appPayMethod !== "") {
            claim =
                this.customDstProcess === null
                    ? dstProcess(this.claim)
                    : this.customDstProcess(this.claim);
        }

        const s3env = this.config.url;

        // Intentionally bypass shared customProcessClaim for this form

        if (claim.flags.valid) {
            grecaptcha.enterprise
                .execute(reCAPTCHAv3, { action: "submit" })
                .then((token) => {
                    claim.data.token = token;
                    this.post_claim(claim, s3env);
                });
        } else {
            document.getElementById("submit-claim").disabled = false;
            modal(
                i18n.getTranslation("error"),
                i18n.getTranslation("claim_form_errors")
            );
        }
    }

    static createInstance({
        appClaimForm,
        userLanguage,
        customInitClaim = null,
        customProcessClaim = null,
        customLoadMember = null,
        customDstProcess = null,
        errorHandler = new ErrorHandler(),
    }) {
        return new customSubmitClaim(
            appClaimForm,
            userLanguage,
            customInitClaim,
            customProcessClaim,
            customLoadMember,
            customDstProcess,
            errorHandler
        );
    }
}
